/**
 * Les propositions d'un QCM qui ne sont qu'un nombre (« $8{,}4$ m », « 45° », « $-3$ », « $\dfrac{5}{6}$ ») reçoivent
 * leur valeur (`value`) quand le modèle publié ne la porte pas : sans elle, la feuille ne peut pas marquer ✓ la
 * proposition que le brouillon établit. 1 962 feuilles `packs` publiées sur 6 290 n'avaient aucune proposition
 * lisible (relevé du 29/10/2026, conversion packs-v3). Lecture du texte seulement : le moteur compare ensuite la
 * valeur à celles que les lignes de l'élève établissent.
 *
 * Prudence (audit du 29/10/2026 : une première version faisait cocher une MAUVAISE réponse à 71 feuilles) : les
 * valeurs ne sont ajoutées que si elles désignent les propositions sans ambiguïté —
 *   - aucune proposition du modèle n'est déjà lisible (valeur, ligne, écriture `exact`) : l'auteur a choisi ;
 *   - une seule grandeur cherchée, une seule solution (« x = 4 ou x = −4 » : « 4 » seul est une réponse fausse) ;
 *   - l'unité est une vraie unité (« 4 seulement », « $10\pi$ » ne sont pas « 4 », « 10 ») ;
 *   - la bonne réponse a sa valeur (sinon seules des mauvaises pourraient être cochées) ;
 *   - deux propositions n'ont pas la même valeur (« $\dfrac{60}{72}$ » vaut « $\dfrac{5}{6}$ » sans être simplifiée).
 */
import type { ScratchModel } from '@jeraisonne/reasoner';

/** Unités reconnues après le nombre (pas « % » : 12 % peut valoir 0,12 pour la feuille). */
const UNITS = new Set(
  (
    'mm cm dm m km µm nm mm² cm² dm² m² km² mm³ cm³ dm³ m³ ha ' +
    'mg g kg t mL cL dL L l mol mmol g/mol g/L mol/L kg/m³ g/cm³ ' +
    's ms min h j an ans °C K ° € ' +
    'V mV kV A mA Ω kΩ W kW MW Wh kWh J kJ MJ N kN Pa hPa kPa bar Hz kHz MHz ' +
    'm/s km/h m/s² N/kg cm/s'
  ).split(' '),
);

/** Le reste d'un libellé après le nombre : une unité reconnue (ou rien) ? */
function unitOk(rest: string): boolean {
  const u = rest
    .replace(/\\(?:Omega)\b/g, 'Ω')
    .replace(/\\mu\b/g, 'µ')
    .replace(/[{}\s]/g, '')
    .replace(/\.$/, '');
  return u === '' || UNITS.has(u);
}

/** La valeur d'une proposition qui n'est qu'un nombre, sinon null. PUR. */
export function choiceValueOf(label: string): number | null {
  let t = label
    .replace(/\\text\{([^}]*)\}/g, ' $1 ')
    .replace(/\\(mathrm|operatorname)\{([^}]*)\}/g, ' $2 ')
    .replace(/\{,\}/g, ',')
    .replace(/\\[,;:! ]/g, '')
    .replace(/\$/g, ' ')
    .replace(/\*\*/g, '')
    .replace(/[  ]/g, ' ')
    .trim();
  if (/%/.test(t)) return null;
  // fraction simple
  const frac = /^([−-])?\s*\\d?frac\{\s*(\d+)\s*\}\{\s*(\d+)\s*\}(.*)$/.exec(t);
  if (frac) {
    const den = Number(frac[3]);
    if (!den || !unitOk(frac[4] ?? '')) return null;
    return (frac[1] ? -1 : 1) * (Number(frac[2]) / den);
  }
  t = t
    .replace(/\\(circ|degree)\b/g, '°')
    .replace(/\^\s*°/g, '°')
    // unité au carré, au cube (« cm$^2$ ») — après une lettre seulement : « 12^2 » vaut 144, pas 12
    .replace(/([a-zA-Zµ])\s*\^\s*\{?\s*2\s*\}?/g, '$1²')
    .replace(/([a-zA-Zµ])\s*\^\s*\{?\s*3\s*\}?/g, '$1³');
  const m = /^([−-])?\s*(\d{1,3}(?:[ ]\d{3})+|\d+)(?:[,.](\d+))?(.*)$/.exec(t);
  if (!m) return null;
  const rest = (m[4] ?? '').trim();
  // « 12² », « 2^3 » : une puissance, pas un nombre et son unité
  if (/^[²³^]/.test(rest)) return null;
  // « 4 seulement », « 10π », « 2,5 cm et 3 cm » : pas une valeur
  if (!unitOk(rest)) return null;
  const n = Number(`${m[2]!.replace(/ /g, '')}.${m[3] ?? '0'}`);
  return Number.isFinite(n) ? (m[1] ? -n : n) : null;
}

const same = (a: number, b: number) => Math.abs(a - b) < 1e-9;

/** Le modèle, ses propositions complétées par leur valeur quand aucune n'est lisible et que ce sont des nombres. */
export function withChoiceValues(model: ScratchModel): ScratchModel {
  const choices = model.choices;
  if (!choices?.length) return model;
  const kernel = (model as { kernel?: { plugin?: string; answer?: string } }).kernel;
  // une feuille d'affirmations tranche ses propositions elle-même
  if (kernel?.plugin === 'claims') return model;
  // l'auteur a rendu des propositions lisibles : c'est son choix
  if (
    choices.some(
      (c) => c.value !== undefined || c.line || (c as { exact?: unknown }).exact !== undefined,
    )
  )
    return model;
  // plusieurs grandeurs cherchées (x₁, x₂, x₃) : on ne sait pas laquelle les propositions donnent
  if ((model.goal ?? []).length > 1) return model;
  // plusieurs solutions : une seule d'entre elles est une réponse fausse
  if (/\sou\s/.test(kernel?.answer ?? '')) return model;
  const values = choices.map((c) => choiceValueOf(c.label ?? ''));
  if (choices.some((c, i) => c.correct && values[i] === null)) return model;
  const known = values.filter((v): v is number => v !== null);
  if (known.some((v, i) => known.some((w, j) => j > i && same(v, w)))) return model;
  return {
    ...model,
    choices: choices.map((c, i) => (values[i] === null ? c : { ...c, value: values[i]! })),
  };
}
